import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import {
  DeleteOutlined,
  EyeOutlined,
  PlusOutlined,
  QuestionCircleOutlined,
} from "@ant-design/icons";
import { uuid } from "@ant-design/plots/es/utils";
import { Button, Dropdown, Space, Tag } from "antd";
import { DotVerticalIcon } from "assets/icons";

import AppButton from "../../../components/button";
import ModalForm from "../../../components/forms/components/modalForm";
import AppTable from "../../../components/table";
import { STATUS_TYPE, USER_TYPE } from "../../../constants/appConstant";
import { AuthInfo } from "../../../stores/slices/auth";
import { setHeaderTitle, setIsRefresh } from "../../../stores/slices/layout";
import {
  doDeleteMessageTemplate,
  doGetMessageTemplatePage,
  numOfEls,
} from "../../../stores/slices/messageTemplate";
import { getWorkspaceInfo } from "../../../stores/slices/workspace";

import MessageTemplateForm from "./messageTemplateForm";

import styles from "../../styles.module.scss";

const MessageTemplatesPage = () => {
  const { t } = useTranslation();
  const params = useParams();
  const dispatch = useDispatch();

  const authInfo = useSelector(AuthInfo);
  const noOfElements = useSelector(numOfEls);

  const [columns, setColumns] = useState([]);
  const [isView, setIsView] = useState(false);
  const [messageTemplateSelected, setMessageTemplateSelected] = useState(null);
  const [isOpenForm, setIsOpenForm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [workspaceInfo, setWorkspaceInfo] = useState(null);
  const [loadingList, setLoadingList] = useState(false);

  const [searchParam, setSearchParam] = useState();
  const [isOpenModalDelete, setIsOpenModalDelete] = useState(false);
  const [selectedMessageTemplate, setSelectedMessageTemplate] = useState();

  const handleSearch = (selectedKeys, confirm) => {
    confirm();
  };

  const handleFilter = (selectedKeys, confirm) => {
    confirm();
  };

  const handleTableChange = (pagination, filters, sorter) => {
    let newSearchParam = {
      statuses: [STATUS_TYPE.ACTIVE],
      workspaceId:
        params.id !== "all"
          ? params.id === "default"
            ? authInfo.workspaceId
            : params.id
          : null,
    };
    if (authInfo.type === USER_TYPE.SUPER_ADMIN) {
      filters.workspaceName &&
        (newSearchParam = {
          ...newSearchParam,
          searchKeyWorkspaceName: filters.workspaceName[0],
        });
    }
    filters.category &&
      (newSearchParam = {
        ...newSearchParam,
        searchKeyCategory: filters.category[0],
      });
    filters.title &&
      (newSearchParam = {
        ...newSearchParam,
        searchKeyTitle: filters.title[0],
      });
    filters.message &&
      (newSearchParam = {
        ...newSearchParam,
        searchKeyMessage: filters.message[0],
      });
    filters.tags &&
      (newSearchParam = {
        ...newSearchParam,
        searchKeyTags: filters.tags[0],
      });

    if (sorter && sorter.columnKey && sorter.order) {
      newSearchParam = {
        ...newSearchParam,
        sortField: sorter.columnKey,
        sortDirection: sorter.order === "ascend" ? "ASC" : "DESC",
      };
    } else {
      newSearchParam = {
        ...newSearchParam,
        sortField: "createdTime",
        sortDirection: "DESC",
      };
    }

    newSearchParam = {
      ...newSearchParam,
      pageNumber: pagination.current,
      pageSize: pagination.pageSize,
    };
    setSearchParam(newSearchParam);
  };

  const actionOptionItems = record => {
    return [
      {
        key: "1",
        label: <span>{t("messageTemplates.viewAndEdit")}</span>,
        icon: <EyeOutlined />,
        onClick: () => {
          setIsView(true);
          setMessageTemplateSelected(record);
          setIsOpenForm(true);
        },
      },

      {
        key: "2",
        danger: true,
        label: <span>{t("messageTemplates.delete")}</span>,
        icon: <DeleteOutlined />,
        onClick: () => {
          setSelectedMessageTemplate(record);
          setIsOpenModalDelete(true);
        },
      },
    ];
  };

  const generateTableColumns = () => {
    let cols = [];
    if (authInfo.type === USER_TYPE.SUPER_ADMIN) {
      cols.push({
        title: t("messageTemplates.workspace"),
        width: 80,
        dataIndex: "workspaceName",
        key: "workspaceName",
        sorter: true,
        isSearchable: true,
        searchPlaceholder:
          "Search by " +
          t("messageTemplates.workspace").toString().toLowerCase(),
        ellipsis: {
          showTitle: true,
        },
        render: (_, { workspaceName }) => {
          return <>{workspaceName || "-"}</>;
        },
      });
    }
    cols = [
      ...cols,
      {
        title: t("messageTemplates.category"),
        width: 80,
        dataIndex: "category",
        key: "category",
        sorter: true,
        isSearchable: true,
        searchPlaceholder:
          "Search by " +
          t("messageTemplates.category").toString().toLowerCase(),
        ellipsis: {
          showTitle: true,
        },
        render: (_, { category }) => {
          return <>{category.category || "-"}</>;
        },
      },
      {
        title: t("messageTemplates.messageTitle"),
        width: 80,
        dataIndex: "title",
        key: "title",
        sorter: true,
        isSearchable: true,
        searchPlaceholder:
          "Search by " +
          t("messageTemplates.messageTitle").toString().toLowerCase(),
        ellipsis: {
          showTitle: true,
        },
      },
      {
        title: t("messageTemplates.message"),
        width: 80,
        dataIndex: "message",
        key: "message",
        // sorter: true,
        isSearchable: true,
        ellipsis: {
          showTitle: true,
        },
      },
      {
        title: t("messageTemplates.totalCharacters"),
        width: 45,
        key: "totalCharacters",
        render: (_, { message }) => <>{message ? message.length : "0"}</>,
      },
      {
        title: t("messageTemplates.keywords"),
        width: 80,
        dataIndex: "tags",
        key: "tags",
        sorter: true,
        isSearchable: true,
        searchPlaceholder:
          "Search by " +
          t("messageTemplates.keywords").toString().toLowerCase(),
        ellipsis: {
          showTitle: false,
        },
        render: (_, { tags }) => (
          <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
            {tags &&
              tags.split(",").map(tag => {
                let color = "geekblue";
                return (
                  <Tag
                    color={color}
                    key={uuid()}
                    bordered={false}
                    style={{ borderRadius: "16px" }}
                  >
                    {tag}
                  </Tag>
                );
              })}
          </div>
        ),
      },
      {
        title: t("messageTemplates.actions"),
        width: 40,
        render: (_, record) => {
          return (
            <Space size="small">
              <Dropdown
                trigger={["click"]}
                menu={{
                  items: actionOptionItems(record),
                }}
                placement="bottomRight"
              >
                <Button type="text" shape="circle" size="middle">
                  <DotVerticalIcon />
                </Button>
              </Dropdown>
            </Space>
          );
        },
      },
    ];
    setColumns(cols);
  };

  const handleDelete = async record => {
    const { payload } = await dispatch(
      doDeleteMessageTemplate({ ids: record.id.toString() })
    );
    setIsOpenModalDelete(false);
    setSelectedMessageTemplate();
    if (payload && payload.status === 200) {
      if (searchParam.pageNumber > 1 && noOfElements === 1) {
        const newSearchParam = {
          ...searchParam,
          pageNumber: searchParam.pageNumber - 1,
        };
        setSearchParam(newSearchParam);
      }
      dispatch(setIsRefresh(uuid()));
      setIsDeleting(false);
    } else {
      setIsDeleting(false);
    }
  };

  const getWorkspaceDetail = async workspaceId => {
    const { payload } = await dispatch(getWorkspaceInfo(workspaceId));
    return payload;
  };

  useEffect(() => {
    dispatch(setHeaderTitle(["messages", "messageTemplates"]));

    if (authInfo && authInfo.id) {
      if (params.id !== "all" && params.id !== "null") {
        getWorkspaceDetail(
          params.id === "default" ? authInfo.workspaceId : params.id
        ).then(res => {
          setWorkspaceInfo(res);
        });
      } else {
        generateTableColumns();
      }
      setSearchParam({
        searchKey: "",
        sortField: ["createdTime"],
        sortDirection: "DESC",
        pageNumber: 1,
        pageSize: 10,
        statuses: [STATUS_TYPE.ACTIVE],
        workspaceId:
          params.id !== "all"
            ? params.id === "default"
              ? authInfo.workspaceId
              : params.id
            : null,
      });
    }
  }, [authInfo]);

  useEffect(() => {
    generateTableColumns();
  }, [workspaceInfo, noOfElements]);

  const handleTitleDelete = () => {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
        }}
      >
        <QuestionCircleOutlined style={{ color: "red", fontSize: 24 }} />
        <span style={{ marginLeft: 10, fontSize: 16 }}>
          {t("messageTemplates.deleteMessageTemplateTitle")}
        </span>
      </div>
    );
  };

  return (
    <div className={styles.messageTemplatesPage}>
      <div className={styles.btnAddNew}>
        <AppButton
          label={t("button.add")}
          color={"primary"}
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => {
            setIsView(false);
            setMessageTemplateSelected(null);
            setIsOpenForm(true);
          }}
          style={{
            minWidth: "auto",
          }}
          // disabled={authInfo.type === USER_TYPE.SUPER_ADMIN}
        />
      </div>
      {columns && (
        <AppTable
          api={doGetMessageTemplatePage(searchParam)}
          columns={columns}
          searchParam={searchParam}
          handleSearch={handleSearch}
          handleFilter={handleFilter}
          handleTableChange={handleTableChange}
          setSearchParam={setSearchParam}
        />
      )}

      <ModalForm
        title={
          messageTemplateSelected && !isView
            ? t("messageTemplates.editPopupTitle")
            : messageTemplateSelected && isView
            ? t("messageTemplates.viewPopupTitle")
            : t("messageTemplates.addNewPopupTitle")
        }
        isOpen={isOpenForm}
        onCancel={() => setIsOpenForm(false)}
        footer={false}
        destroyOnClose={true}
        labelSubmit={t("label.save")}
        maskClosable={false}
      >
        <MessageTemplateForm
          isViewForm={isView}
          userType={authInfo.type}
          workspaceId={authInfo?.workspaceId}
          workspaceInfo={workspaceInfo ? workspaceInfo.name : null}
          loadingList={loadingList}
          setLoadingList={setLoadingList}
          messageTemplateSelected={messageTemplateSelected}
          onUpdate={() => {
            setIsView(false);
          }}
          onCancel={() => {
            setMessageTemplateSelected(null);
            setIsOpenForm(false);
          }}
          onSuccess={() => {
            dispatch(setIsRefresh(uuid()));
            setIsOpenForm(false);
          }}
        />
      </ModalForm>
      <ModalForm
        isOpen={isOpenModalDelete}
        type="confirm"
        title={handleTitleDelete()}
        onCancel={() => {
          setIsOpenModalDelete(false);
        }}
        onSubmit={() => {
          handleDelete(selectedMessageTemplate);
        }}
        okType={"danger"}
        danger={true}
        maskClosable={false}
        footer={[
          <AppButton
            key="button"
            label={t("button.cancel")}
            color={"grey"}
            loading={isDeleting}
            disabled={isDeleting}
            htmlType="button"
            onClick={() => {
              setIsOpenModalDelete(false);
            }}
          />,
          <AppButton
            key="button"
            color="red"
            label={t("button.delete")}
            loading={isDeleting}
            disabled={isDeleting}
            onClick={() => {
              setIsDeleting(true);
              handleDelete(selectedMessageTemplate);
            }}
          />,
        ]}
      >
        <div style={{ height: 50, marginLeft: 35, fontSize: 14 }}>
          {t("messageTemplates.deleteMessageTemplateMsg")}
        </div>
      </ModalForm>
      {/* <ModalForm
        title={t("messageTemplates.addNewCategoryPopupTitle")}
        isOpen={isOpenCategoryForm}
        onCancel={() => setIsOpenCategoryForm(false)}
        footer={false}
        destroyOnClose={true}
        labelSubmit={t("label.save")}
      >
        <CategoryForm setIsOpenCategoryForm={setIsOpenCategoryForm} />
      </ModalForm> */}
    </div>
  );
};
export default MessageTemplatesPage;
